<script lang="ts">
  import { onMount } from 'svelte';
  import {
    drawChangeLoopLimaconBottom,
    drawChangeLoopLimaconTop,
    drawParagraphLimacon,
  } from './limaconFunctions.js';
  import {
    figureData,
    STROKE_COLOR_PRIMARY,
    STROKE_COLOR_SECONDARY,
    STROKE_WIDTH,
    FIGURE_SCALE,
    GRID_COLOR,
    GRID_STROKE_WIDTH,
  } from './figureData.js';

  interface Props {
    figures?: Array<{ title?: string; subtitle?: string }>;
  }

  let { figures: figureOverrides = [] }: Props = $props();

  // Get figures 5, 6, 7 (ROCKER, CHANGE LOOP, PARAGRAPH LOOP)
  const figures = figureData.slice(5, 8).map((fig, i) => ({
    ...fig,
    title: figureOverrides[i]?.title || fig.title,
    subtitle: figureOverrides[i]?.subtitle || fig.subtitle,
  }));

  let changeLoopLimaconBottomPathData = $state('');
  let changeLoopLimaconTopPathData = $state('');
  let changeLoopLimaconBottomPathLength = $state(0);
  let changeLoopLimaconTopPathLength = $state(0);
  let paragraphLimaconPathData = $state('');
  let paragraphLimaconPathLength = $state(0);
  let visibleFigures = $state([]);
  let containerElement: HTMLElement;

  onMount(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(
              (entry.target as HTMLElement).dataset.index || '0'
            );
            if (!visibleFigures.includes(index)) {
              visibleFigures = [...visibleFigures, index];

              // Original animation timing for indices 5, 6, 7
              let baseDelay = 0;
              if (index === 0) baseDelay = 3030; // Originally index 5 (ROCKER)
              if (index === 1) baseDelay = 3780; // Originally index 6 (CHANGE LOOP)
              if (index === 2) baseDelay = 4530; // Originally index 7 (PARAGRAPH LOOP)

              // Draw limacon paths for CHANGE LOOP (now local index 1)
              if (index === 1) {
                const changeLoopBottom = drawChangeLoopLimaconBottom();
                changeLoopLimaconBottomPathData = changeLoopBottom.pathData;
                changeLoopLimaconBottomPathLength = changeLoopBottom.pathLength;

                const changeLoopTop = drawChangeLoopLimaconTop();
                changeLoopLimaconTopPathData = changeLoopTop.pathData;
                changeLoopLimaconTopPathLength = changeLoopTop.pathLength;
              } else if (index === 2) {
                // PARAGRAPH LOOP (now local index 2)
                const paragraph1 = drawParagraphLimacon();
                paragraphLimaconPathData = paragraph1.pathData;
                paragraphLimaconPathLength = paragraph1.pathLength;
              }

              // Split into two animation patterns: limacon (indices 1,2) vs regular (index 0)
              if (index === 1 || index === 2) {
                // Limacon figures need 50ms setTimeout for DOM update after path generation
                setTimeout(() => {
                  const svg = entry.target.querySelector('svg');
                  if (svg) {
                    const animateElements = svg.querySelectorAll('animate');
                    animateElements.forEach((anim, animIndex) => {
                      let delay = baseDelay;

                      // Index 1: CHANGE LOOP (original index 6) - 4 animates
                      if (index === 1) {
                        if (animIndex === 0) delay += 0;
                        else if (animIndex === 1) delay += 3750;
                        else if (animIndex === 2) delay += 1250;
                        else if (animIndex === 3) delay += 5000;
                      }
                      // Index 2: PARAGRAPH LOOP (original index 7) - 4 animates
                      else if (index === 2) {
                        if (animIndex === 0) delay += 0;
                        else if (animIndex === 1) delay += 2500;
                        else if (animIndex === 2) delay += 5000;
                        else if (animIndex === 3) delay += 7500;
                      }

                      setTimeout(() => {
                        anim.beginElement();
                      }, delay);
                    });
                  }
                }, 50); // 50ms delay for limacon path DOM update
              } else {
                // Regular figure: ROCKER (index 0, original index 5)
                const svg = entry.target.querySelector('svg');
                if (svg) {
                  const animateElements = svg.querySelectorAll('animate');
                  animateElements.forEach((anim, animIndex) => {
                    let delay = baseDelay;

                    // Index 0: ROCKER (original index 5) - 8 animates (4 paths with 2 layers each)
                    // Grouped by animIndex ranges for 4 sequential parts
                    if (animIndex === 4 || animIndex === 5)
                      delay += 0; // Top circle solid layer
                    else if (animIndex === 0 || animIndex === 1)
                      delay += 1875; // Bottom circle solid layer
                    else if (animIndex === 6 || animIndex === 7)
                      delay += 4500; // Middle left solid layer
                    else if (animIndex === 2 || animIndex === 3) delay += 6125; // Middle right solid layer

                    setTimeout(() => {
                      anim.beginElement();
                    }, delay);
                  });
                }
              }
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    // Only observe cards within this component's container
    if (containerElement) {
      const cards = containerElement.querySelectorAll('.figure-card');
      cards.forEach((card) => observer.observe(card));
    }

    return () => observer.disconnect();
  });
</script>

<div bind:this={containerElement} class="figures-wrapper container">
  <div class="figures-grid">
    {#each figures as figure, index}
      <div class="figure-card" data-index={index}>
        <div class="figure-title">{figure.title}</div>
        <div class="figure-subtitle">{figure.subtitle}</div>
        <svg
          width={140 * FIGURE_SCALE}
          height={(figure.height / 2) * FIGURE_SCALE}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 280 {figure.height}"
        >
          {#if figure.useParagraphLoop}
            <g>
              <line
                x1="140"
                y1="0"
                x2="140"
                y2="500"
                stroke={GRID_COLOR}
                stroke-width={GRID_STROKE_WIDTH}
                stroke-dasharray="5,5"
              />
              <line
                x1="0"
                y1="250"
                x2="280"
                y2="250"
                stroke={GRID_COLOR}
                stroke-width={GRID_STROKE_WIDTH}
                stroke-dasharray="5,5"
              />

              <g transform="rotate(90 140 250)">
                <path
                  d={paragraphLimaconPathData}
                  fill="none"
                  stroke={STROKE_COLOR_PRIMARY}
                  stroke-width={STROKE_WIDTH}
                  stroke-dasharray={paragraphLimaconPathLength}
                  stroke-dashoffset={paragraphLimaconPathLength}
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from={paragraphLimaconPathLength}
                    to="0"
                    begin="indefinite"
                    dur="2.5s"
                    fill="freeze"
                  />
                </path>
              </g>

              <g transform="rotate(-90 140 250)">
                <path
                  d={paragraphLimaconPathData}
                  fill="none"
                  stroke={STROKE_COLOR_PRIMARY}
                  stroke-width={STROKE_WIDTH}
                  stroke-dasharray={paragraphLimaconPathLength}
                  stroke-dashoffset={paragraphLimaconPathLength}
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from={paragraphLimaconPathLength}
                    to={paragraphLimaconPathLength * 2}
                    begin="indefinite"
                    dur="2.5s"
                    fill="freeze"
                  />
                </path>
              </g>

              <g transform="rotate(90 140 250)">
                <path
                  d={paragraphLimaconPathData}
                  fill="none"
                  stroke={STROKE_COLOR_SECONDARY}
                  stroke-width={STROKE_WIDTH}
                  stroke-dasharray={paragraphLimaconPathLength}
                  stroke-dashoffset={paragraphLimaconPathLength}
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from={paragraphLimaconPathLength}
                    to="0"
                    begin="indefinite"
                    dur="2.5s"
                    fill="freeze"
                  />
                </path>
              </g>

              <g transform="rotate(-90 140 250)">
                <path
                  d={paragraphLimaconPathData}
                  fill="none"
                  stroke={STROKE_COLOR_SECONDARY}
                  stroke-width={STROKE_WIDTH}
                  stroke-dasharray={paragraphLimaconPathLength}
                  stroke-dashoffset={paragraphLimaconPathLength}
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from={paragraphLimaconPathLength}
                    to={paragraphLimaconPathLength * 2}
                    begin="indefinite"
                    dur="2.5s"
                    fill="freeze"
                  />
                </path>
              </g>
            </g>
          {:else if figure.useChangeLoop}
            <g transform="rotate(180 140 250)">
              <line
                x1="140"
                y1="0"
                x2="140"
                y2="500"
                stroke={GRID_COLOR}
                stroke-width={GRID_STROKE_WIDTH}
                stroke-dasharray="5,5"
              />
              <line
                x1="0"
                y1="250"
                x2="280"
                y2="250"
                stroke={GRID_COLOR}
                stroke-width={GRID_STROKE_WIDTH}
                stroke-dasharray="5,5"
              />

              <path
                d="M 140,160 A 70,70 0 0,1 140,300"
                fill="none"
                stroke={STROKE_COLOR_PRIMARY}
                stroke-width={STROKE_WIDTH}
                stroke-dasharray="220"
                stroke-dashoffset="220"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="220"
                  to="0"
                  begin="indefinite"
                  dur="1.25s"
                  fill="freeze"
                />
              </path>

              <path
                d="M 140,300 A 70,70 0 0,1 140,160"
                fill="none"
                stroke={STROKE_COLOR_SECONDARY}
                stroke-width={STROKE_WIDTH}
                stroke-dasharray="220"
                stroke-dashoffset="220"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="220"
                  to="0"
                  begin="indefinite"
                  dur="1.25s"
                  fill="freeze"
                />
              </path>

              <g transform="rotate(-90 140 250)">
                <path
                  d={changeLoopLimaconBottomPathData}
                  fill="none"
                  stroke={STROKE_COLOR_PRIMARY}
                  stroke-width={STROKE_WIDTH}
                  stroke-dasharray={changeLoopLimaconBottomPathLength}
                  stroke-dashoffset={changeLoopLimaconBottomPathLength}
                >
                  <animate
                    attributeName="stroke-dashoffset"
                    from={changeLoopLimaconBottomPathLength}
                    to={changeLoopLimaconBottomPathLength * 2}
                    begin="indefinite"
                    dur="2.5s"
                    fill="freeze"
                  />
                </path>

                <g transform="rotate(180 248 205)">
                  <path
                    d={changeLoopLimaconTopPathData}
                    fill="none"
                    stroke={STROKE_COLOR_SECONDARY}
                    stroke-width={STROKE_WIDTH}
                    stroke-dasharray={changeLoopLimaconTopPathLength}
                    stroke-dashoffset={changeLoopLimaconTopPathLength}
                  >
                    <animate
                      attributeName="stroke-dashoffset"
                      from={changeLoopLimaconTopPathLength}
                      to="0"
                      begin="indefinite"
                      dur="2.5s"
                      fill="freeze"
                    />
                  </path>
                </g>
              </g>
            </g>
          {:else}
            {@html figure.svg}
          {/if}
        </svg>
      </div>
    {/each}
  </div>
</div>

<style>
  .figures-wrapper {
    overflow-x: hidden;
  }

  .container {
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .figures-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 30px;
    margin-top: 30px;
    justify-items: center;
  }

  @media (max-width: 768px) {
    .figures-grid {
      gap: 0;
    }

    .container {
      padding: 10px;
    }

    .figure-card {
      padding: 5px;
    }

    .figure-card svg {
      width: 100%;
      max-width: 100px;
      height: auto;
    }

    .figure-title {
      font-size: 0.875em;
      min-height: 2.5em;
    }

    .figure-subtitle {
      display: none;
    }
  }

  .figure-card {
    padding: 5px;
    border-radius: 8px;
    width: 100%;
    max-width: 400px;
    display: flex;
    flex-direction: column;
  }

  .figure-title {
    text-align: center;
    font-weight: bold;
    color: #2c3e50;
    font-size: 1.2em;
    min-height: 1.5em;
  }

  .figure-subtitle {
    text-align: center;
    margin-bottom: 15px;
    font-size: 1em;
    color: #666;
    min-height: 3em;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  svg {
    display: block;
    margin: 0 auto;
    margin-top: auto;
    width: 100%;
    height: auto;
    max-width: 100%;
  }
</style>
